// middleware/v3-session.global.ts — route guard for the V3 flow (SPEC §7)
// It also forwards every legacy V1 route into V3 (see LEGACY_REDIRECTS): the
// legacy tree still ships, but nothing can reach it any more.
import { defineNuxtRouteMiddleware, navigateTo } from '#imports'
import { useSessionStore } from '~/store/v3/session'
import { useBootstrapStore } from '~/store/v3/bootstrap'

const PUBLIC_PATHS = new Set(['/v3/qrcode', '/v3/login', '/v3/invalid'])

/**
 * Legacy V1 route -> its V3 counterpart.
 *
 * Old bookmarks, home-screen shortcuts and browser history still point at these
 * paths. Without this they render the V1 pages, which boot the whole legacy
 * store tree and then strand the guest on a login screen V3 cannot read (V1
 * keeps its token in localStorage `token`, V3 in `xtable.v3.session`).
 *
 * Redirecting here rather than from the pages themselves means the guard runs
 * before the router loads the route component, so the legacy chunk is never
 * even fetched — and the legacy files stay untouched, ready to be deleted in
 * one commit when the time comes.
 *
 * `/` and `/qrcode` (the URL printed on the tables) are listed here too; the
 * forwarder pages behind them are now a fallback that never renders.
 */
const LEGACY_REDIRECTS: Record<string, string> = {
  '/': '/v3',
  '/qrcode': '/v3/qrcode',
  '/login': '/v3/login',
  '/add': '/v3/menu',
  '/summary': '/v3/summary',
  '/pos': '/v3', // the employee flow is gone in V3 — let the guard place the guest
}

/** '/v3/menu/' -> '/v3/menu' (keeps '/' intact). */
const normalize = (path: string): string => path.replace(/\/+$/, '') || '/'

const firstQueryValue = (v: unknown): string => {
  if (Array.isArray(v)) return typeof v[0] === 'string' ? v[0] : ''
  return typeof v === 'string' ? v : ''
}

export default defineNuxtRouteMiddleware((to) => {
  // ssr:false, but never let this run on the server.
  if (import.meta.server) return

  const path = normalize(to.path)

  // legacy route -> V3, query and all (a `?token=` is picked up by the rule below)
  const legacyTarget = LEGACY_REDIRECTS[path]
  if (legacyTarget) {
    return navigateTo({ path: legacyTarget, query: to.query }, { replace: true })
  }

  if (path !== '/v3' && !path.startsWith('/v3/')) return

  // a `?token=` anywhere is an entry attempt — hand it to the entry page
  if (path !== '/v3/qrcode' && firstQueryValue(to.query.token)) {
    return navigateTo({ path: '/v3/qrcode', query: to.query }, { replace: true })
  }

  if (PUBLIC_PATHS.has(path)) return

  const session = useSessionStore()
  const bootstrap = useBootstrapStore()

  session.restore()

  // no session -> let the guest type an ID; '/v3/invalid' is for a rejected one
  if (!session.token) {
    return navigateTo('/v3/login', { replace: true })
  }

  if (session.status === 'ended') {
    return navigateTo(session.endReason === 'invalid' ? '/v3/invalid' : '/v3/login', { replace: true })
  }

  if (session.isExpired()) {
    session.end('expired')
    return navigateTo('/v3/login', { replace: true })
  }

  if (path !== '/v3' && bootstrap.status !== 'ready') {
    return navigateTo({ path: '/v3', query: { redirect: to.fullPath } })
  }
})
